import { combine, Store } from "effector";
import { add, differenceInDays, min, startOfDay, sub } from "date-fns";
import dinero, { Dinero } from "dinero.js";
import { apply as applyJsonLogic } from "json-logic-js";
import { $selectedLocation } from "./selectedLocations";
import { $selectedPackages } from "./selectedPackages";
import { $selectedActivities } from "./selectedActivities";
import { $selectedSupplements } from "./selectedSupplements";
import { AppConfig } from "../../config";

const $allProducts = combine(
  $selectedPackages,
  $selectedActivities,
  $selectedSupplements
);

const $packagesActivities = combine($selectedPackages, $selectedActivities);

export const $totalCost: Store<Dinero> = $allProducts.map((data) => {
  const [packages, activities, supplements] = data;
  let res = dinero();

  packages.forEach(({ availabilitySlot: { totalPrice } }) => {
    res = res.add(totalPrice);
  });
  activities.forEach(({ availabilitySlot: { totalPrice } }) => {
    res = res.add(totalPrice);
  });
  supplements.forEach(({ totalPrice }) => {
    res = res.add(totalPrice);
  });

  return res;
});

export const $totalTaxes: Store<Dinero> = $allProducts.map((data) => {
  const [packages, activities, supplements] = data;
  let res = dinero();

  packages.forEach(({ availabilitySlot: { totalTax } }) => {
    res = res.add(totalTax);
  });
  activities.forEach(({ availabilitySlot: { totalTax } }) => {
    res = res.add(totalTax);
  });
  supplements.forEach(({ totalTax }) => {
    res = res.add(totalTax);
  });

  return res;
});

export const $subTotalCost = combine($totalCost, $totalTaxes).map((data) => {
  const [cost, taxes] = data;
  return cost.subtract(taxes);
});

export const $instancesIds = $packagesActivities.map((data) => {
  const [packages, activities] = data;
  const instancesIds: string[] = [];

  packages.forEach(({ instancesIds: ids }) => {
    instancesIds.push(...ids);
  });
  activities.forEach(({ instancesIds: ids }) => {
    instancesIds.push(...ids);
  });

  return instancesIds;
});

export type SupplementsServingVariants = {
  packages: {
    id: number;
    name: string;
    activities: {
      id: number;
      name: string;
    }[];
  }[];
  activities:
    | {
        id: number;
        name: string;
      }[]
    | null;
};
export const $supplementsServingVariants: Store<SupplementsServingVariants> = $packagesActivities.map(
  (data) => {
    const [packages, activities] = data;

    const res: SupplementsServingVariants = { packages: [], activities: [] };

    packages.forEach(({ entity }) => {
      const activities = entity.activities.map(({ id, name }) => ({
        id,
        name,
      }));

      res.packages.push({
        id: entity.id,
        name: entity.name,
        activities,
      });
    });
    activities.forEach(({ entity }) => {
      if (res.activities) {
        res.activities.push({ id: entity.id, name: entity.name });
      } else {
        res.activities = [{ id: entity.id, name: entity.name }];
      }
    });

    res.activities =
      res.activities && res.activities?.length > 0 ? res.activities : null;

    return res;
  }
);

export const $prepayment: Store<{
  date: Date;
  amount: Dinero;
} | null> = combine(
  $totalCost,
  $selectedLocation,
  $selectedPackages,
  $selectedActivities
).map((data) => {
  const [total, location, packages, activities] = data;

  if (total === null || !location?.paymentRules) {
    return null;
  }
  // percents - 1 = 100% ; 0.5 = 50%
  const [dueDateDaysAfterBooking, percents]: [number, number] = applyJsonLogic(
    location.paymentRules,
    {
      total: total.getAmount() / 100,
    }
  );

  const amount = total.multiply(percents);

  if (amount.isZero()) {
    return null;
  }

  const firstServiceStart: Date[] = [];
  packages.forEach((package_) => {
    firstServiceStart.push(package_.availabilitySlot.date);
  });
  activities.forEach((activities_) => {
    firstServiceStart.push(activities_.availabilitySlot.date);
  });
  const firstServiceStartDate = min(firstServiceStart);
  const date = calculateDueDate(
    dueDateDaysAfterBooking,
    firstServiceStartDate,
    location.no_prepayment_hours
  );
  return { date, amount: amount };
});

const calculateDueDate = (
  dueDateDaysAfterBooking: number,
  reservationDate: Date,
  noPrepaymentHours: number
) => {
  let dueDate = add(new Date(), { days: dueDateDaysAfterBooking });

  const daysUntilDueDate = differenceInDays(dueDate, new Date());
  if (daysUntilDueDate > AppConfig.maxDaysInFutureDueDateMollie) {
    dueDate = add(new Date(), { days: AppConfig.maxDaysInFutureDueDateMollie });
  }

  if (dueDate > reservationDate) {
    dueDate = startOfDay(
      sub(new Date(reservationDate), { hours: noPrepaymentHours })
    );
  }

  if (dueDate < new Date()) {
    dueDate = new Date();
  }

  return dueDate;
};
